import React, { useMemo, useCallback } from 'react';
import { Select } from '@iq/react-components';
import '../widgets/SelectWidget.scss';
import './ComponentPickerField.scss';

const ComponentPickerField = (props) => {
  const {
    disabled,
    readonly,
    required = true,
    formData: value,
    schema: {
      handleCreatable,
      components = [],
      default: defaultValue,
      isMulti,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      label,
      valueKey = 'id',
      styles = {},
      modalState,
      editable,
    },
    onChange,
    ...otherProps
  } = props;
  const getLabel = (val) => {
    const selectedComponent = components.find(({ id }) => id === val);
    return selectedComponent
      ? `${selectedComponent.name} (${selectedComponent.itemDesignation})`
      : val;
  };

  const opts = useMemo(
    () =>
      components.map((component) => ({
        value: component[valueKey] || component.id,
        label: `${component.name} (${component.itemDesignation})`,
      })),
    [components, valueKey]
  );

  const theValue = useMemo(() => {
    if (isMulti) {
      return (value || []).map((v) => {
        const selectedComponent = components.find(({ id }) => id === v);
        return {
          value: v,
          label: selectedComponent
            ? `${selectedComponent.name} (${selectedComponent.itemDesignation})`
            : v,
        };
      });
    }
    if (value || value === 0) {
      return { label: getLabel(value), value };
    }
    if (defaultValue) {
      return { label: getLabel(defaultValue), defaultValue };
    }
    return undefined;
  }, [value, defaultValue, components]);

  // Get selected components for display mode
  const selectedComponents = useMemo(() => {
    if (isMulti && value && value.length > 0) {
      return value.map((v) => components.find(({ id }) => id === v)).filter(Boolean);
    }
    if (value || value === 0) {
      const selectedComponent = components.find(({ id }) => id === value);
      return selectedComponent ? [selectedComponent] : [];
    }
    return [];
  }, [value, components, isMulti]);

  const setValues = useCallback(
    (selected) => {
      if (isMulti) {
        onChange((selected || []).map((s) => s.value));
      } else {
        onChange(selected && (selected.value || selected.value === 0) ? selected.value : null);
      }
    },
    [onChange, isMulti]
  );

  const getEmptyMsg = (text) => <div>{text}</div>;

  const overrideStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, lineHeight: 'normal', ...styles.container }),
      singleValue: (base) => ({ ...base, lineHeight: 'normal', ...styles.singleValue }),
      control: (base) => ({ ...base, lineHeight: 'normal', ...styles.control }),
    }),
    [styles]
  );

  const isViewMode = editable === false || modalState === 'view' || readonly || disabled;

  const LabelComponent = ({ showRequired = true }) =>
    label && (
      <label className="control-label">
        {label}
        {required && showRequired && <span className="required">*</span>}
      </label>
    );

  const ViewModeContainer = ({ children }) => (
    <div className="component-picker-field">
      <LabelComponent showRequired={false} />
      {children}
    </div>
  );

  if (isViewMode) {
    return (
      <ViewModeContainer>
        {selectedComponents.length > 0 ? (
          <div className="selected-components-display">
            {selectedComponents.map((component) => (
              <div
                key={component.id}
                className="selected-component-item"
              >
                <span className="component-name">{component.name}</span>
                <span className="component-designation">({component.itemDesignation})</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="selected-components-display">No components selected</div>
        )}
      </ViewModeContainer>
    );
  }

  return (
    <div className="component-picker-field">
      <LabelComponent />
      <Select
        {...otherProps}
        className="schema-select-widget"
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={theValue}
        onChange={setValues}
        onCreateOption={handleCreatable}
        options={opts}
        noOptionsMessage={() => getEmptyMsg(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </div>
  );
};

export default ComponentPickerField;
